import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X } from 'lucide-react';
import {
  STOCK_CATEGORIES,
  STOCK_UNITS,
  parseQuantity,
  parseRupees,
  type StockCategory,
  type StockUnit,
} from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, Card, CardContent, Input, Label, useToast } from '@tailoring-crm/ui';
import { useCreateStockItem } from '../lib/inventory/use-stock.js';

/**
 * Adding something to the shelf.
 *
 * The form asks for colours as a list rather than making the shop create one item per colour,
 * which is the modelling decision the whole module rests on: everything else about the cloth —
 * supplier, rate, unit, reorder level — is the same for all of them.
 *
 * It does NOT ask how much there is. A new item starts at zero and the first purchase is a
 * movement, because a quantity typed at creation would be the one number in this module with no
 * row behind it.
 */
export function NewStockItemPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const create = useCreateStockItem();

  const [name, setName] = useState('');
  const [category, setCategory] = useState<StockCategory>('fabric');
  const [unit, setUnit] = useState<StockUnit>('metre');
  const [rate, setRate] = useState('');
  const [reorderLevel, setReorderLevel] = useState('');
  const [location, setLocation] = useState('');
  const [colours, setColours] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);

  function submit(): void {
    setError(null);
    const variants = colours
      .map((colour) => colour.trim())
      .filter((colour) => colour.length > 0)
      .map((colour) => ({ colour }));

    if (name.trim().length === 0 || variants.length === 0) {
      setError(t('stock.nameAndColourRequired'));
      return;
    }

    create
      .mutateAsync({
        name: name.trim(),
        category,
        unit,
        purchaseRatePaise: rate ? parseRupees(rate) : 0,
        reorderLevel: reorderLevel ? (parseQuantity(reorderLevel) ?? 0) : 0,
        ...(location.trim() ? { location: location.trim() } : {}),
        variants,
      })
      .then((item) => {
        toast({ title: t('stock.created', { name: item.name }) });
        void navigate(`/stock/${item.id}`);
      })
      .catch(() => {
        setError(t('error.unexpected'));
      });
  }

  return (
    <>
      <PageHeader
        title={t('stock.addTitle')}
        description={t('stock.addDescription')}
        action={
          <Button asChild variant="ghost">
            <Link to="/stock">
              <ArrowLeft aria-hidden="true" />
              {t('stock.backToList')}
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-3 pt-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-name">{t('stock.nameLabel')}</Label>
            <Input id="stock-name" value={name} onChange={(event) => setName(event.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-category">{t('stock.categoryLabel')}</Label>
            <select
              id="stock-category"
              value={category}
              onChange={(event) => setCategory(event.target.value as StockCategory)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              {STOCK_CATEGORIES.map((one) => (
                <option key={one} value={one}>
                  {t(`stock.category.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-unit">{t('stock.unitLabel')}</Label>
            <select
              id="stock-unit"
              value={unit}
              onChange={(event) => setUnit(event.target.value as StockUnit)}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
            >
              {STOCK_UNITS.map((one) => (
                <option key={one} value={one}>
                  {t(`stock.unit.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-rate">{t('stock.rateLabel', { unit })}</Label>
            <Input
              id="stock-rate"
              inputMode="decimal"
              value={rate}
              onChange={(event) => setRate(event.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-reorder">{t('stock.reorderLabel')}</Label>
            <Input
              id="stock-reorder"
              inputMode="decimal"
              value={reorderLevel}
              onChange={(event) => setReorderLevel(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t('stock.reorderHint')}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-location">{t('stock.locationLabel')}</Label>
            <Input
              id="stock-location"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">{t('stock.coloursLabel')}</legend>
            <p className="text-xs text-muted-foreground">{t('stock.coloursHint')}</p>
            {colours.map((colour, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={colour}
                  aria-label={t('stock.colourNumber', { number: index + 1 })}
                  onChange={(event) => {
                    const next = [...colours];
                    next[index] = event.target.value;
                    setColours(next);
                  }}
                />
                {colours.length > 1 ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('stock.removeColour')}
                    onClick={() => setColours(colours.filter((_, at) => at !== index))}
                  >
                    <X aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
            ))}
            <div>
              <Button variant="outline" size="sm" onClick={() => setColours([...colours, ''])}>
                <Plus aria-hidden="true" />
                {t('stock.addColour')}
              </Button>
            </div>
          </fieldset>

          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div>
            <Button disabled={create.isPending} onClick={submit}>
              {t('stock.create')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export default NewStockItemPage;
